/**
 * useMetadataModal Hook
 *
 * Purpose: Custom hook for metadata modal state management
 * Architecture: Manages modal lifecycle, form submission, dirty state tracking
 * Dependencies: React, MetadataModal types
 * Author: Development Team
 * Created: 2025-01-31
 */

import { useState, useCallback } from 'react';
import type { FormData, UseMetadataModalReturn } from './types';
import type { MetadataModalProps } from './types';

/**
 * Custom hook for metadata modal management
 *
 * Purpose: Provides modal state management, submission handling, and unsaved changes tracking
 * Logic: Manages submission state, dirty tracking, and modal close behavior
 * Input: MetadataModalProps configuration
 * Output: Modal state and handlers
 * Exceptions: Catches and stores submission errors
 */
export const useMetadataModal = (props: MetadataModalProps): UseMetadataModalReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  // Determine if we should warn about unsaved changes
  const shouldWarnOnClose = props.warnOnUnsavedChanges ?? props.mode !== 'view';

  /**
   * Handle modal close
   *
   * Purpose: Close modal with unsaved changes check
   * Logic: Shows warning if form is dirty, otherwise closes immediately
   * Input: force - if true, closes without warning
   */
  const handleClose = useCallback(
    (force: boolean = false) => {
      if (isSubmitting) return; // Prevent close during submission

      // Check for unsaved changes
      if (!force && shouldWarnOnClose && isDirty) {
        setShowUnsavedWarning(true);
        return;
      }

      // Close modal
      props.onClose();

      // Reset state on close after animation delay
      setTimeout(() => {
        setSubmitError(null);
        setSubmitSuccess(false);
        setIsDirty(false);
        setShowUnsavedWarning(false);
        setFormKey((prev) => prev + 1);
      }, 300);
    },
    [isSubmitting, shouldWarnOnClose, isDirty, props]
  );

  /**
   * Handle confirm close from unsaved changes dialog
   *
   * Purpose: Force close modal when user confirms leaving
   */
  const handleConfirmClose = useCallback(() => {
    setShowUnsavedWarning(false);
    handleClose(true); // Force close
  }, [handleClose]);

  /**
   * Handle cancel close from unsaved changes dialog
   *
   * Purpose: Keep modal open when user cancels leaving
   */
  const handleCancelClose = useCallback(() => {
    setShowUnsavedWarning(false);
  }, []);

  /**
   * Handle form data change
   *
   * Purpose: Track dirty state for unsaved changes warning
   * Logic: Compares current form data with initial data
   * Input: currentData - current form state, initialData - initial form state
   */
  const handleFormChange = useCallback((currentData: FormData, initialData: FormData) => {
    // Simple dirty check using JSON comparison
    const isDirtyState = JSON.stringify(currentData) !== JSON.stringify(initialData);
    setIsDirty(isDirtyState);
  }, []);

  /**
   * Handle form submission
   *
   * Purpose: Submit form data and handle success/error states
   * Logic: Calls onSubmit prop, closes modal on success
   * Input: formData - form data to submit
   * Exceptions: Catches and displays submission errors
   */
  const handleSubmit = useCallback(
    async (formData: FormData) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        if (props.onSubmit) {
          await props.onSubmit(formData);
        }
        setSubmitSuccess(true);
        setIsDirty(false); // Clear dirty state on successful submit
        props.onSuccess?.(formData);

        // Close modal after brief delay to show success state
        setTimeout(() => {
          handleClose(true); // Force close without warning
        }, 500);
      } catch (error: any) {
        console.error('Form submission error:', error);
        setSubmitError(error.message || 'Failed to save. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [props, handleClose]
  );

  return {
    isSubmitting,
    submitError,
    submitSuccess,
    formKey,
    isDirty,
    showUnsavedWarning,
    handleClose,
    handleConfirmClose,
    handleCancelClose,
    handleFormChange,
    handleSubmit,
  };
};
